import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  DraftQuoteRequest,
  DraftQuoteResult,
  EstimateResponse,
} from '@rheora/enrollment-contract';

import { getEstimate, postDraftQuote, type EstimateArgs } from './estimate.fetchers';
import { funnelQueryKeys } from './funnel.queries';

/**
 * TanStack hooks for E-SF04. Pages call THESE, never the fetchers.
 *
 *  - The ESTIMATE is a read-only query (a GET) keyed on candidate + plan +
 *    profile + the DECLARED monthly kWh, so a re-view of the same premise/plan at
 *    the same usage reuses the cache and a CHANGED usage does not.
 *  - The DRAFT QUOTE create is a mutation (a POST with an idempotency key + a side
 *    effect): the funnel fires it when the customer proceeds past the estimate, so
 *    the sign step has a quoteId.
 */

export const estimateQueryKeys = {
  /**
   * ⚠️ THE DECLARED kWh IS PART OF THE KEY (E-SF19 P0a). It is an INPUT to the
   * price, so leaving it out would serve a shopper who changed their usage the
   * bill for the figure they changed away from — a stale number under the words
   * "at your usage", which is the same class of defect this epic exists to close,
   * arriving through the cache instead of through the wire.
   */
  estimate: (args: EstimateArgs) =>
    [
      'shop',
      'estimate',
      args.candidateId,
      args.planId,
      args.homeProfile ?? null,
      args.declaredMonthlyKwh ?? null,
    ] as const,
} as const;

/**
 * The personalization estimate. `enabled` is false until a candidate + plan are
 * known (the page is unreachable without a resolved premise). Not retried on the
 * client's account — the estimate hits a rate-limited, credentialed upstream.
 */
export function useEstimateQuery(args: EstimateArgs | null): UseQueryResult<EstimateResponse> {
  return useQuery({
    queryKey: args === null ? ['shop', 'estimate', 'disabled'] : estimateQueryKeys.estimate(args),
    queryFn: () => {
      if (args === null) throw new Error('estimate query fired without a candidate/plan');
      return getEstimate(args);
    },
    enabled: args !== null,
    retry: false,
  });
}

/**
 * The DRAFT quote-create mutation (blocker 3).
 *
 * Invalidates the funnel snapshot because the stored `quoteId` is precisely the
 * server-side evidence that the estimate step completed — it is what advances
 * `GET /bff/funnel`'s derived `step` to `sign`.
 */
export function useCreateDraftQuoteMutation(): UseMutationResult<
  DraftQuoteResult,
  Error,
  DraftQuoteRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postDraftQuote,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}
